/**
 * AX1 Company Model Types - Phase 1 of 2026 Access Model
 *
 * Purpose: Define TypeScript interfaces for Companies as first-class entities.
 * Companies are organizations (clients, partners, investors, vendors, internal firm)
 * that participate in the consulting and capital-raising platform.
 *
 * Relationships:
 *   - Users belong to Companies via company_members (N:M)
 *   - Companies relate to Projects via company_relationships
 *   - Companies are workspace-scoped for multi-tenancy
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Company type determines whether this is the internal firm or an external entity
 * This is the "Scope" dimension.
 */
export type CompanyType = 'internal' | 'external';

/**
 * Company-level business role classification.
 * Separate from Scope (internal/external) - a company can be external AND be a vendor.
 * Used for:
 *   - Filtering companies by what they do
 *   - Pre-filling relationship_type when linking to projects
 */
export type CompanyRole =
  | 'capital_recipient'
  | 'partner'
  | 'vendor'
  | 'investor';

/**
 * Canonical company-to-project/investment relationship type.
 * This is the authoritative type for domain model and UI logic.
 * Does not include 'client' (historical database value only).
 * 'internal' is preserved for the platform's own entity on legacy links.
 */
export type ProjectCompanyRelationshipType =
  | 'capital_recipient'
  | 'portfolio_company'
  | 'borrower'
  | 'sponsor'
  | 'project_company'
  | 'investee'
  | 'vendor'
  | 'partner'
  | 'investor'
  | 'internal';

/**
 * ⚠️ RESTRICTED: Raw storage value permitted ONLY at persistence/repository boundaries.
 * Includes the legacy 'client' value that may exist in historical database rows.
 *
 * Do NOT import this in:
 *   ❌ components
 *   ❌ pages
 *   ❌ hooks
 *   ❌ authorization logic
 *   ❌ route guards
 *   ❌ normal service callers
 *
 * ONLY allowed in:
 *   ✅ repository/RPC adapters (read boundary)
 *   ✅ migration code
 *   ✅ explicit backward-compatibility tests
 *   ✅ types/company.ts (for normalizer definition)
 *
 * After hydration, use ProjectCompanyRelationshipType instead.
 * See normalizeProjectCompanyRelationship() for the one-way mapping.
 */
export type LegacyProjectCompanyRelationshipValue =
  | ProjectCompanyRelationshipType
  | 'client';

/**
 * Canonical alias for active code. Same as ProjectCompanyRelationshipType.
 * If you need the legacy value, you are at the wrong boundary.
 * Call normalizeProjectCompanyRelationship() at the persistence layer instead.
 */
export type RelationshipType = ProjectCompanyRelationshipType;

/**
 * How a user is affiliated with a company
 */
export type AffiliationType = 'employee' | 'contractor' | 'representative' | 'advisor';

/**
 * Engagement level for company-project relationships
 */
export type EngagementType = 'primary' | 'secondary' | 'advisory' | 'observer';

/**
 * Status for entities
 */
export type CompanyStatus = 'active' | 'inactive' | 'archived';
export type MemberStatus = 'active' | 'inactive' | 'pending';
export type RelationshipStatus = 'active' | 'inactive' | 'pending' | 'completed';

/**
 * Company size tier for classification (LinkedIn-style employee ranges)
 */
export type SizeTier = 
  | '1-10'        // Self-employed / Startup
  | '11-50'       // Small
  | '51-200'      // Small-Medium
  | '201-500'     // Medium
  | '501-1000'    // Medium-Large
  | '1001-5000'   // Large
  | '5001-10000'  // Enterprise
  | '10001+';     // Enterprise+

/**
 * Organization type: structural classification of the company
 */
export type CompanyOrgType = 
  | 'operating'         // Standard operating company
  | 'holding_group'     // Parent/holding entity
  | 'investor_vc'       // Investment firm, VC, PE
  | 'subsidiary'        // Owned subsidiary
  | 'portfolio_company' // Investment portfolio company
  | 'spv';              // Special purpose vehicle

/**
 * Parent relationship type: how a company relates to its parent
 */
export type CompanyParentRelationshipType = 
  | 'subsidiary'        // Owned subsidiary
  | 'portfolio_company' // Portfolio investment
  | 'division'          // Business division/unit
  | 'spv';              // Special purpose vehicle

// ============================================================================
// COMPANY
// ============================================================================

/**
 * Company - A first-class organization entity
 */
export interface Company {
  id: string;
  workspace_id: string;

  // Identity
  name: string;
  legal_name?: string;
  domain?: string; // Primary email domain for auto-linking
  logo_url?: string;

  // Classification
  company_type: CompanyType;
  org_type?: CompanyOrgType;
  
  // Business personas (portal, ally, supplier, capital)
  // Separate from scope - used for filtering and prefilling project links
  roles?: CompanyRole[];

  // Hierarchy (parent company relationship)
  parent_id?: string;
  parent_relationship_type?: CompanyParentRelationshipType;

  // Metadata
  industry?: string;
  size_tier?: SizeTier;
  website?: string;
  description?: string;

  // Contact
  primary_contact_email?: string;
  primary_contact_phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state_province?: string;
  postal_code?: string;
  country?: string;

  // Status
  status: CompanyStatus;

  // Audit
  created_at: string;
  updated_at: string;
  created_by?: string;
}

/**
 * Company creation payload
 */
export interface CompanyCreate {
  workspace_id: string;
  name: string;
  legal_name?: string;
  domain?: string;
  logo_url?: string;
  company_type?: CompanyType;
  org_type?: CompanyOrgType;
  roles?: CompanyRole[];
  parent_id?: string;
  parent_relationship_type?: CompanyParentRelationshipType;
  industry?: string;
  size_tier?: SizeTier;
  website?: string;
  description?: string;
  primary_contact_email?: string;
  primary_contact_phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state_province?: string;
  postal_code?: string;
  country?: string;
  status?: CompanyStatus;
}

/**
 * Company update payload (all fields optional)
 */
export type CompanyUpdate = Partial<Omit<Company, 'id' | 'created_at' | 'updated_at'>>;

// ============================================================================
// COMPANY MEMBER
// ============================================================================

/**
 * CompanyMember - User's affiliation with a company
 */
export interface CompanyMember {
  id: string;
  company_id: string;
  user_id: string;

  // Affiliation
  affiliation_type: AffiliationType;
  company_title?: string; // e.g., "VP of Engineering", "CEO"
  department?: string;

  // Status
  is_primary: boolean;
  status: MemberStatus;

  // Dates
  joined_at: string;
  left_at?: string;

  // Audit
  created_at: string;
  updated_at: string;
  added_by?: string;
}

/**
 * Company member with joined company data
 */
export interface CompanyMemberWithCompany extends CompanyMember {
  company: Company;
}

/**
 * Company member with joined user data
 */
export interface CompanyMemberWithUser extends CompanyMember {
  user: {
    id: string;
    name: string;
    email: string;
    avatar_url?: string;
    role: string;
    employment_type?: string;
  };
}

/**
 * Company member creation payload
 */
export interface CompanyMemberCreate {
  company_id: string;
  user_id: string;
  affiliation_type?: AffiliationType;
  company_title?: string;
  department?: string;
  is_primary?: boolean;
  status?: MemberStatus;
}

/**
 * Company member update payload
 */
export type CompanyMemberUpdate = Partial<Omit<CompanyMember, 'id' | 'company_id' | 'user_id' | 'created_at' | 'updated_at'>>;

// ============================================================================
// COMPANY RELATIONSHIP
// ============================================================================

/**
 * CompanyRelationship - How a company relates to a workspace or project
 */
export interface CompanyRelationship {
  id: string;
  company_id: string;
  workspace_id: string;
  project_id?: string; // NULL = workspace-level

  // Relationship
  relationship_type: RelationshipType;
  engagement_type?: EngagementType;
  contract_reference?: string;

  // Capital raising context (Phase 4)
  capital_round_id?: string;

  // Status
  status: RelationshipStatus;
  effective_from?: string;
  effective_to?: string;

  // Audit
  created_at: string;
  updated_at: string;
  created_by?: string;
}

/**
 * Company relationship with joined company data
 */
export interface CompanyRelationshipWithCompany extends CompanyRelationship {
  company: Company;
}

/**
 * Company relationship creation payload
 */
export interface CompanyRelationshipCreate {
  company_id: string;
  workspace_id: string;
  project_id?: string;
  relationship_type: RelationshipType;
  engagement_type?: EngagementType;
  contract_reference?: string;
  capital_round_id?: string;
  status?: RelationshipStatus;
  effective_from?: string;
  effective_to?: string;
}

/**
 * Company relationship update payload
 */
export type CompanyRelationshipUpdate = Partial<Omit<CompanyRelationship, 'id' | 'company_id' | 'created_at' | 'updated_at'>>;

// ============================================================================
// HELPER TYPES
// ============================================================================

/**
 * User's company affiliations summary (from get_user_companies RPC)
 */
export interface UserCompanyAffiliation {
  company_id: string;
  company_name: string;
  company_type: CompanyType;
  affiliation_type: AffiliationType;
  is_primary: boolean;
  /** Full company object (included in demo mode) */
  company?: Company;
}

/**
 * Project's company participants (from get_project_companies RPC)
 */
export interface ProjectCompanyParticipant {
  company_id: string;
  company_name: string;
  relationship_type: RelationshipType;
  engagement_type?: EngagementType;
  status: RelationshipStatus;
  /** If true, members of this company can see other companies linked to the project */
  can_view_company_list?: boolean;
}

/**
 * Project's company with visibility context (from get_project_companies_for_user RPC)
 * Used when displaying company lists to users with visibility restrictions.
 * relationship_type may be a legacy 'client' value; call normalizeProjectCompanyRelationship()
 * before business logic.
 */
export interface ProjectCompanyForUser {
  company_id: string;
  company_name: string;
  relationship_type: LegacyProjectCompanyRelationshipValue;
  engagement_type?: EngagementType;
  status: RelationshipStatus;
  /** Restricted fields - only shown if can_view_full_details is true */
  website?: string;
  country?: string;
  company_type?: CompanyType;
  /** True if current user can see full details (their own company or admin/PM) */
  can_view_full_details: boolean;
}

/**
 * Company list item for dropdowns/selectors
 */
export interface CompanyListItem {
  id: string;
  name: string;
  company_type: CompanyType;
  logo_url?: string;
  domain?: string;
  status: CompanyStatus;
}

/**
 * Company with member count for admin views
 */
export interface CompanyWithStats extends Company {
  member_count: number;
  active_project_count: number;
}

// ============================================================================
// CONTEXT TYPES (for React Context/Hooks)
// ============================================================================

/**
 * Company context state
 */
export interface CompanyContextState {
  /** User's primary company */
  primaryCompany: Company | null;
  /** All companies user is affiliated with */
  userCompanies: UserCompanyAffiliation[];
  /** Currently selected company (for multi-company users) */
  activeCompanyId: string | null;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
}

/**
 * Company context actions
 */
export interface CompanyContextActions {
  /** Set active company for multi-company users */
  setActiveCompany: (companyId: string) => void;
  /** Refresh user's company affiliations */
  refreshCompanies: () => Promise<void>;
}

/**
 * Full company context
 */
export type CompanyContext = CompanyContextState & CompanyContextActions;

// ============================================================================
// PERMISSION HELPERS
// ============================================================================

/**
 * ONE-WAY compatibility normalizer.
 * Converts the historical database value 'client' to the canonical domain value.
 *
 * Rules:
 *  - Must only be called at persistence/repository read boundaries.
 *  - Never re-serializes 'capital_recipient' back as 'client'.
 *  - Counted/logged for backfill migration evidence.
 *  - Temporary — remove after database backfill is applied.
 *
 * @param value Raw value read from database (may be 'client' in historical rows)
 * @returns Canonical ProjectCompanyRelationshipType
 */
export function normalizeProjectCompanyRelationship(
  value: LegacyProjectCompanyRelationshipValue
): ProjectCompanyRelationshipType {
  if (value === 'client') {
    // Compatibility: legacy 'client' rows map to 'capital_recipient'.
    // This mapping is one-way; 'client' will never be written back.
    return 'capital_recipient';
  }
  return value;
}

/**
 * Check if a relationship type implies internal access
 */
export function isInternalRelationship(type: ProjectCompanyRelationshipType): boolean {
  return type === 'internal';
}

/**
 * Check if a relationship type implies investor access
 */
export function isInvestorRelationship(type: ProjectCompanyRelationshipType): boolean {
  return type === 'investor';
}

/**
 * Get relationship type display name.
 * Accepts the canonical type only; call normalizeProjectCompanyRelationship() first
 * if you have a raw legacy value.
 */
export function getRelationshipDisplayName(type: ProjectCompanyRelationshipType): string {
  const names: Record<ProjectCompanyRelationshipType, string> = {
    internal: 'Internal Team',
    capital_recipient: 'Investee',
    portfolio_company: 'Portfolio Company',
    borrower: 'Borrower',
    sponsor: 'Sponsor',
    project_company: 'Project Company',
    investee: 'Investee',
    partner: 'Partner',
    investor: 'Investor',
    vendor: 'Vendor',
  };
  return names[type];
}

/**
 * Get affiliation type display name
 */
export function getAffiliationDisplayName(type: AffiliationType): string {
  const names: Record<AffiliationType, string> = {
    employee: 'Employee',
    contractor: 'Contractor',
    representative: 'Representative',
    advisor: 'Advisor'
  };
  return names[type];
}

/**
 * Labels used by early demo records as placeholder titles rather than an
 * actual job title. Keep them out of every environment's UI without changing
 * stored company data.
 */
export function isLegacyCompanyTitle(title?: string | null): boolean {
  return ['team member', 'consultant'].includes(title?.trim().toLowerCase() ?? '');
}

/**
 * Get company type display name
 */
export function getCompanyTypeDisplayName(type: CompanyType): string {
  return type === 'internal' ? 'Internal Firm' : 'External Organization';
}
